import { forwardRef, useRef, useState, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { useReqireLabel } from '../utils';
import type { TrackingProp } from '../types';
import { useTracking, getLabel } from '../utils';
import styles from '../input/input.module.less';

export type TextareaProps = React.ComponentPropsWithoutRef<'textarea'> & {
  classNameWrapper?: string;
  invalid?: boolean;
  maxWarning?: number;
  tracking?: TrackingProp;
};

let valueOnFocus: string; // Used to check if there is actually a change between focus and blur
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      classNameWrapper,
      hidden,
      invalid,
      maxWarning,
      onBlur,
      onFocus,
      tracking = true,
      ...rest
    },
    ref
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    const classes = clsx(styles.input, classNameWrapper, {
      [styles.isInvalid]: invalid
    });
    const track = useTracking(tracking);

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(event);
      valueOnFocus = event.target.value;
    };
    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (valueOnFocus !== event.target.value)
        track({
          eventCategory: 'textarea',
          eventAction: 'change',
          eventLabel: getLabel(event.target)
        });
      onBlur?.(event);
    };

    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement); // Forward innerRef
    useReqireLabel<HTMLTextAreaElement>(innerRef);

    return (
      <div className={classes} hidden={hidden} {...useCount(maxWarning)}>
        <textarea
          aria-invalid={invalid}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={innerRef}
          {...rest}
        />
      </div>
    );
  }
);

const useCount = (max?: number) => {
  const [count, setCount] = useState(0);
  const onInput = ({ target }: React.FormEvent<HTMLDivElement>) => {
    if (target instanceof HTMLTextAreaElement) {
      setCount(target.value.length);
      target.style.height = ''; // Reset to natural height
      target.style.height = `${target.scrollHeight}px`;
    }
  };

  if (!max) return { onInput };
  return {
    'data-count': `${count}/${max}`,
    'data-count-warning': count > max || null,
    onInput
  };
};
